import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, AlertCircle, CheckCircle, FileText, Star, Code, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import DOMPurify from 'dompurify';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Format template type for display
 */
const formatTemplateType = (type) => {
  const typeLabels = {
    'rfq_outreach': 'RFQ Outreach',
    'candidate_reminder': 'Candidate Reminder',
    'client_update': 'Client Update',
    'follow_up': 'Follow Up',
    'interview_invite': 'Interview Invite',
    'rfq_inclusion_request': 'RFQ: Request Inclusion',
    'candidate_bulk_outreach': 'Candidate: Bulk Outreach',
    'candidate_individual_outreach': 'Candidate: Individual Outreach'
  };
  return typeLabels[type] || type.replace(/_/g, ' ');
};

/**
 * EmailTemplates - Manage email templates for outreach
 */
const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'rfq_outreach',
    subject_template: '',
    body_template: '',
    is_default: false
  });
  const [showSource, setShowSource] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Auto-dismiss success message
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/email-templates`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTemplates(data.templates || []);
      } else {
        throw new Error(data.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        type: template.type,
        subject_template: template.subject_template,
        body_template: template.body_template,
        is_default: template.is_default
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        type: 'rfq_outreach',
        subject_template: 'Opportunity: {{rfq.title}}',
        body_template: `<p>Hi {{candidate.first_name}},</p>

<p>I hope this email finds you well. I'm reaching out regarding an exciting opportunity that matches your experience and skills.</p>

<p><strong>Position:</strong> {{rfq.title}}<br>
<strong>Client:</strong> {{rfq.client_name}}<br>
<strong>Location:</strong> {{rfq.locations}}<br>
<strong>Clearance:</strong> {{rfq.clearance_required}}<br>
<strong>Work Arrangement:</strong> {{rfq.work_arrangement}}<br>
<strong>Start Date:</strong> {{rfq.estimated_start_date}}<br>
<strong>Term:</strong> {{rfq.initial_term_months}} months</p>

<p>{{rfq.description}}</p>

<p>If you're interested, you can apply directly here:<br>
{{application_url}}</p>

<p>Please let me know if you'd like to discuss this opportunity further.</p>

<p>Best regards</p>`,
        is_default: false
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      if (editingTemplate) {
        // Update existing template
        const response = await fetch(`${API_URL}/matching/email-templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to update template');
        }

        setSuccess('Template updated successfully');
      } else {
        // Create new template
        const response = await fetch(`${API_URL}/matching/email-templates`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to create template');
        }

        setSuccess('Template created successfully');
      }

      setShowModal(false);
      await fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete template');
      }

      setSuccess('Template deleted successfully');
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Email Templates</h2>
          <p className="text-muted-foreground">
            Create and manage reusable email templates for candidate outreach
          </p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {success}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Templates
          </CardTitle>
          <CardDescription>
            {templates.length} template{templates.length !== 1 ? 's' : ''} available
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No email templates yet</p>
              <Button onClick={() => handleOpenModal()} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Create First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map(template => (
                <div
                  key={template.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-sm">
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                        <span className="text-xs text-muted-foreground">
                          • {formatTemplateType(template.type)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {template.subject_template}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(template)}
                        className="h-8 w-8 p-0"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowModal(false)}
              >
                ×
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., RFQ Outreach - Default"
                  className="w-full border rounded-md px-3 py-2 bg-background"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 bg-background"
                >
                  <optgroup label="General Templates">
                    <option value="rfq_outreach">RFQ Outreach</option>
                    <option value="candidate_reminder">Candidate Reminder</option>
                    <option value="client_update">Client Update</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="interview_invite">Interview Invite</option>
                  </optgroup>
                  <optgroup label="Action Templates">
                    <option value="rfq_inclusion_request">RFQ: Request Inclusion</option>
                    <option value="candidate_bulk_outreach">Candidate: Bulk Outreach</option>
                    <option value="candidate_individual_outreach">Candidate: Individual Outreach</option>
                  </optgroup>
                </select>
                <p className="text-xs text-muted-foreground mt-2">
                  {formData.type === 'rfq_inclusion_request' && 'Used when requesting inclusion in an RFQ via the Request Inclusion button'}
                  {formData.type === 'candidate_bulk_outreach' && 'Used when drafting bulk emails (BCC) to multiple candidates'}
                  {formData.type === 'candidate_individual_outreach' && 'Used when drafting individual emails to candidates'}
                  {!['rfq_inclusion_request', 'candidate_bulk_outreach', 'candidate_individual_outreach'].includes(formData.type) && 'General purpose template for manual use'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Subject Template</label>
                <input
                  type="text"
                  value={formData.subject_template}
                  onChange={(e) => setFormData({ ...formData, subject_template: e.target.value })}
                  placeholder="Opportunity: {{rfq.title}}"
                  className="w-full border rounded-md px-3 py-2 bg-background"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Body Template</label>
                  <div className="inline-flex items-center bg-muted rounded-md p-1">
                    <button
                      type="button"
                      onClick={() => setShowSource(false)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                        !showSource
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Eye className="h-3 w-3 inline mr-1" />
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSource(true)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                        showSource
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Code className="h-3 w-3 inline mr-1" />
                      HTML
                    </button>
                  </div>
                </div>

                {showSource ? (
                  <textarea
                    value={formData.body_template}
                    onChange={(e) => setFormData({ ...formData, body_template: e.target.value })}
                    placeholder="Enter HTML email template with formatting and {{merge.tags}}"
                    className="w-full min-h-[300px] border rounded-md px-3 py-2 bg-white font-mono text-sm"
                    rows={12}
                  />
                ) : (
                  <div className="w-full min-h-[300px] border rounded-md px-4 py-3 bg-white overflow-auto">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.body_template || '<p class="text-muted-foreground">Preview will appear here...</p>') }} />
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-2 space-y-2">
                  <p className="font-medium">Available merge tags:</p>
                  <div className="space-y-1">
                    <p><strong>Candidate:</strong></p>
                    <div className="ml-2 space-x-1">
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{candidate.first_name}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{candidate.last_name}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{candidate.email}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{candidate.location_state}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{candidate.clearance_level}}'}</code>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p><strong>RFQ:</strong></p>
                    <div className="ml-2 space-x-1">
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.ref_code}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.title}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.client_name}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.locations}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.clearance_required}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.work_arrangement}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.client_close_datetime}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.estimated_start_date}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.initial_term_months}}'}</code>
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{rfq.description}}'}</code>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p><strong>Other:</strong></p>
                    <div className="ml-2 space-x-1">
                      <code className="bg-muted px-1 py-0.5 rounded">{'{{application_url}}'}</code>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Set as default template</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end p-4 border-t space-x-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
